//React
import { FunctionComponent, useEffect, useState, useRef } from "react";
//Google Maps
import {
	Map,
	useApiIsLoaded,
	useMap,
	useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { AdvancedMarkerInfoWindow } from "../info-window/advanced-marker-info-window";
//App Hooks
import {
	useAppDispatch,
	useAppSelector,
} from "../../../system/redux/hooks/redux-store-hooks";
//Actions
import {
	setLocationZoomSettings,
	setMapMoved,
	setMapZoomed,
	setWithinRadius,
} from "../../redux/slices/map-settings-slice";
import { setReRenderMap } from "../../../system/redux/slices/app-status-slice";
//Models
import { LocationZoomSettings } from "../../models/map-settings";
//Selectors
import { selectMapMoved } from "../../redux/selectors/select-map-moved";
import { selectMapZoomed } from "../../redux/selectors/select-map-zoomed";
import { selectSelectedResult } from "../../../results/redux/selectors/select-selected-result";
import { selectAllResults } from "../../../results/redux/selectors/select-all-results";
import { selectUnmatchedResults } from "../../../results/redux/selectors/facets/select-unmatched-results";
import { selectRerenderMap } from "../../../system/redux/selectors/select-rerender-map";
import { selectMatchedResults } from "../../../results/redux/selectors/facets/select-matched-results";
import { selectAreaInfo } from "../../../area-info/redux/selectors/select-area-info";
import { selectIsQmiFinder } from "../../../system/redux/selectors/select-is-qmi-finder";
import { selectIsFirstSearchComplete } from "../../../system/redux/selectors/select-first-search-complete";

//Util
import { Circle } from "../debug/debug-map-overlay-circle";
import { getMapRadius } from "../../functions/get-map-radius";
//Components
import MobileInfoWindow from "../info-window/mobile-info-window";
import { selectLocationZoomSettings } from "../../redux/selectors/select-location-zoom-settings";

const pinStyle = {
	boxShadow: "none",
	outline: "none",
};

// Constants: Zoom Levels
const commDefaultZoom = 7;
const commMinZoom = 6;

const hotwDefaultZoom = 12;
const hotwMinZoom = 9;

//Using the DR Horton location as the default center of the map.  Using fitbounds will
// move the map to show all the pins and it looks better if it scrolls from Arlington vs. the middle
// of the ocean which is the base default.
const arlingtonLocation = {
	latitude: 32.76,
	longitude: -97.09,
};

export const GoogleMap: FunctionComponent = (props) => {
	//Store Dispatch
	const dispatch = useAppDispatch();

	//State Values
	const mapMoved = useAppSelector(selectMapMoved);
	const mapZoomed = useAppSelector(selectMapZoomed);
	const allResults = useAppSelector(selectAllResults);
	const matchedResults = useAppSelector(selectMatchedResults);
	const unmatchedResults = useAppSelector(selectUnmatchedResults);
	const reRenderMap = useAppSelector(selectRerenderMap);
	const locationZoomSettings = useAppSelector(selectLocationZoomSettings);
	const isQmiFinder = useAppSelector(selectIsQmiFinder);
	const isFirstSearchComplete = useAppSelector(selectIsFirstSearchComplete);

	/** The user selects a result by hovering the mouse over a result in the result list.
	 * This makes the corresponding pin get bigger. */
	const selectedResult = useAppSelector(selectSelectedResult);

	//Google Map Values
	const apiIsLoaded = useApiIsLoaded();
	const theMap = useMap();
	const places = useMapsLibrary("places");
	const geometry = useMapsLibrary("geometry");
	const showRadiusOverlay = localStorage.getItem("showMapOverlay") !== null;

	const areaInfo = useAppSelector(selectAreaInfo);

	//React Hooks
	const [isLoaded, setIsLoaded] = useState(false);
	const [defaultCenter, setDefaultCenter] = useState({
		lat: arlingtonLocation.latitude,
		lng: arlingtonLocation.longitude,
	});

	const defaultZoom = isQmiFinder ? hotwDefaultZoom : commDefaultZoom;
	const minZoom = isQmiFinder ? hotwMinZoom : commMinZoom;

	// State to manage which marker's info window is open
	const [hoveredMarkerId, setHoveredMarkerId] = useState(null);
	const timeoutRef = useRef(null);

	// Handlers to update the hovered marker ID with a delay
	const handleMarkerHover = (uniqueId) => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
		setHoveredMarkerId(uniqueId);
	};

	const handleMarkerLeave = () => {
		timeoutRef.current = setTimeout(() => {
			setHoveredMarkerId(null);
		}, 80);
	};

	// Use Effects
	// This useEffect handles fitting bounds, which is already correct.
	useEffect(() => {
		if ((!mapMoved && !mapZoomed) || reRenderMap) {
			if (isLoaded && theMap !== null) {
				if (allResults?.length > 0) {
					var bounds = new google.maps.LatLngBounds();
					allResults?.forEach((location) => {
						if (
							location?.position?.latitude !== 0 &&
							location?.position?.longitude !== 0
						) {
							bounds.extend({
								lat: location?.position?.latitude,
								lng: location?.position?.longitude,
							});
						}
					});
					theMap.fitBounds(bounds);

					//When we are using fit bound
					setTimeout(() => {
						updateWithinRadiusFromMap();
					}, 80);
				} else {
					//THis means there are coords from the URL in Redux
					if (locationZoomSettings.lat !== 0) {
						let newCenter = new google.maps.LatLng({
							lat: locationZoomSettings?.lat,
							lng: locationZoomSettings?.lng,
						});

						theMap.setCenter(newCenter);
						theMap.setZoom(locationZoomSettings.zoom);
					} else {
						//This means there are not results but an area info came back with lat/long/zoom
						if (areaInfo?.Latitude !== 0) {
							theMap.setCenter(
								new google.maps.LatLng({
									lat: areaInfo?.Latitude,
									lng: areaInfo?.Longitude,
								}),
							);

							if (areaInfo?.Zoom !== 0) {
								theMap.setZoom(areaInfo?.Zoom);
							}

							updateZoomRadiusSettingsFromAreaInfo();
						} else {
							theMap.setCenter(
								new google.maps.LatLng({
									lat: arlingtonLocation?.latitude,
									lng: arlingtonLocation?.longitude,
								}),
							);
						}
					}
				}

				dispatch(setReRenderMap(false));
			}
		}
	}, [isLoaded, allResults, theMap, mapZoomed, reRenderMap, areaInfo]);

	useEffect(() => {
		if (isLoaded) {
			return;
		}
		let loadedStatus = getLoadedStatus();
		if (loadedStatus) {
			if (locationZoomSettings.lat !== 0) {
				setDefaultCenter(locationZoomSettings);
			}
			setIsLoaded(loadedStatus);
		}
	}, [apiIsLoaded, theMap]);

	//Functions
	function getLoadedStatus() {
		return apiIsLoaded;
	}

	const updateLocationZoomSettingsFromMap = () => {
		let center = theMap?.getCenter();
		let zoom = theMap?.getZoom();
		let lat = center.lat();
		let lng = center.lng();
		let newLocationZoomSettings: LocationZoomSettings = {
			lat: lat,
			lng: lng,
			zoom: zoom,
			radius: getMapRadius(theMap),
		};
		//Set the redux map settings
		dispatch(setLocationZoomSettings(newLocationZoomSettings));
	};

	//This updates the within X miles radius, this is not using the mapsettings radius
	// because when we set that it kicks off another api call so for urls like
	// //texas/dallas two api calls will be made unnecessarily
	const updateWithinRadiusFromMap = () => {
		dispatch(setWithinRadius(getMapRadius(theMap)));
	};

	const updateZoomRadiusSettingsFromAreaInfo = () => {
		let newLocationZoomSettings: LocationZoomSettings = {
			lat: 0,
			lng: 0,
			zoom: areaInfo?.Zoom,
			radius: areaInfo?.Radius,
		};
		//Set the redux map settings
		dispatch(setLocationZoomSettings(newLocationZoomSettings));
	};

	//When the map is dragged update the location in the redux state
	const handleMapDragged = () => {
		if (!isLoaded) {
			return;
		}
		//Set the redux map moved flag
		dispatch(setMapMoved(true));
		updateLocationZoomSettingsFromMap();
	};

	//When the map zoom changes, update the location in the redux state
	const handleMapZoomed = () => {
		if (!isLoaded) {
			return;
		}
		//The first time the map is zoomed can be from the fitBounds call
		//this ignores the first zoom as far as setting the location settings in redux.
		if (!mapMoved && !mapZoomed) {
			//Set the redux map zoomed flag
			dispatch(setMapZoomed(true));
			return;
		}
		//Set the redux map zoomed flag
		dispatch(setMapZoomed(true));
		updateLocationZoomSettingsFromMap();
	};

	function checkForSelected(uniqueId: string) {
		if (selectedResult === null) {
			return false;
		}
		return selectedResult?.uniqueId === uniqueId;
	}

	function getZindex(uniqueId: string, defaultZindex: number): number {
		if (selectedResult === null) {
			return defaultZindex;
		}
		if (selectedResult?.uniqueId !== uniqueId) {
			return defaultZindex;
		}
		return 500;
	}

	function getOpacity(uniqueId: string, defaultOpacity: number): number {
		if (selectedResult === null) {
			return defaultOpacity;
		}
		if (selectedResult?.uniqueId !== uniqueId) {
			return defaultOpacity;
		}
		return 1;
	}

	//JSX
	return (
		<section className="poi-map panel">
			{(!isLoaded) ? (
				<div>Loading...</div>
			) : (
				<div className="map" style={pinStyle}>
					<Map
						clickableIcons={false}
						disableDefaultUI={true}
						//We are using fitToBounds to move the map around so when the API results
						// come back the map will auto adjust to fit all the pins.
						//Defaulting to Dallas so when the FitBounds call is made and the map moves
						// it will not start in the middle of the ocean.
						defaultCenter={defaultCenter}
						defaultZoom={defaultZoom}
						minZoom={minZoom}
						maxZoom={12}
						zoomControl={true}
						mapId="f911eed1f36afa1e3645009d"
						className="CoveoCommunityFinderMap community__coveo-map"
						onZoomChanged={handleMapZoomed}
						onDragend={handleMapDragged}
						gestureHandling="greedy"
					>
						{/* Output markers that match the current facet filters */}
						{theMap && isFirstSearchComplete &&
							matchedResults?.map((result) => (
								<AdvancedMarkerInfoWindow
									key={result.uniqueId}
									result={result}
									markerOpacity={getOpacity(result.uniqueId, 1)}
									zIndex={getZindex(result.uniqueId, 100)}
									selected={checkForSelected(result.uniqueId)}
									mapCenter={{
										lat: theMap.getCenter().lat(),
										lng: theMap.getCenter().lng(),
									}}
									zoomLevel={locationZoomSettings.zoom}
									onMarkerHover={handleMarkerHover}
									onMarkerLeave={handleMarkerLeave}
									isInfoWindowOpen={hoveredMarkerId === result.uniqueId}
								/>
							))}

						{/* Output markers that DO NOT match the current facet filters */}
						{theMap && isFirstSearchComplete &&
							unmatchedResults?.map((result) => (
								<AdvancedMarkerInfoWindow
									key={result.uniqueId}
									result={result}
									markerOpacity={getOpacity(result.uniqueId, 0.33)}
									zIndex={getZindex(result.uniqueId, 1)}
									selected={checkForSelected(result.uniqueId)}
									mapCenter={{
										lat: theMap.getCenter().lat(),
										lng: theMap.getCenter().lng(),
									}}
									zoomLevel={locationZoomSettings.zoom}
									onMarkerHover={handleMarkerHover}
									onMarkerLeave={handleMarkerLeave}
									isInfoWindowOpen={hoveredMarkerId === result.uniqueId}
								/>
							))}
						{isFirstSearchComplete && <MobileInfoWindow result={selectedResult} />}
					</Map>
				</div>
			)}
		</section>
	);
};

export default GoogleMap;
